// ═══════════════════════════════════════════════════════════════
// DrawingController — Custom click-to-draw geometry on Leaflet
// Modes: polygon, polyline, circle, corridor (buffered line)
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import { useMapEvents, Polyline, CircleMarker, Circle, Polygon } from 'react-leaflet';
import { circleToPolygon, polylineLengthFromVertices, polygonArea, bufferLine } from '../../lib/geoopsGeo';

export type DrawMode = 'idle' | 'polygon' | 'polyline' | 'circle' | 'corridor';

export interface LiveMeasurement {
  mode: DrawMode;
  /** Current radius in meters when drawing a circle */
  radiusMeters?: number;
  /** Total length in meters of an in-progress polyline / corridor */
  lengthMeters?: number;
  /** Area in square meters of an in-progress polygon */
  areaSqMeters?: number;
  /** Number of vertices placed so far */
  vertexCount?: number;
}

interface DrawingControllerProps {
  mode: DrawMode;
  /** Buffer radius in meters for corridor mode */
  corridorBufferMeters?: number;
  onComplete: (geometry: GeoJSON.Geometry) => void;
  onCancel: () => void;
  /** Fired on every move/click while drawing so the toolbar can show live numbers */
  onMeasureUpdate?: (m: LiveMeasurement) => void;
}

export default function DrawingController({
  mode, corridorBufferMeters = 402, onComplete, onCancel, onMeasureUpdate,
}: DrawingControllerProps) {
  const [vertices, setVertices] = useState<[number, number][]>([]);
  const [circleCenter, setCircleCenter] = useState<[number, number] | null>(null);
  const [circleRadius, setCircleRadius] = useState(0);
  // Live cursor for previewing the next segment / polygon area while drawing
  const [hoverPoint, setHoverPoint] = useState<[number, number] | null>(null);

  // Reset when mode changes
  useEffect(() => {
    setVertices([]);
    setCircleCenter(null);
    setCircleRadius(0);
    setHoverPoint(null);
    onMeasureUpdate?.({ mode });
  }, [mode]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Escape to cancel + Enter to finish
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setVertices([]);
        setCircleCenter(null);
        onCancel();
      }
      if (e.key === 'Enter' && vertices.length >= 2) {
        finishDrawing();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertices, mode]);

  const finishDrawing = useCallback(async () => {
    if (mode === 'polygon' && vertices.length >= 3) {
      const coords = vertices.map(([lat, lng]) => [lng, lat]);
      coords.push(coords[0]);
      onComplete({ type: 'Polygon', coordinates: [coords] });
      setVertices([]);
    } else if (mode === 'polyline' && vertices.length >= 2) {
      const coords = vertices.map(([lat, lng]) => [lng, lat]);
      onComplete({ type: 'LineString', coordinates: coords });
      setVertices([]);
    } else if (mode === 'corridor' && vertices.length >= 2) {
      const coords = vertices.map(([lat, lng]) => [lng, lat]);
      const line: GeoJSON.LineString = { type: 'LineString', coordinates: coords };
      const buffered = await bufferLine(line, corridorBufferMeters);
      if (buffered) {
        onComplete(buffered);
      } else {
        // Fallback: just save the line so the user doesn't lose work
        onComplete(line);
      }
      setVertices([]);
    }
  }, [vertices, mode, corridorBufferMeters, onComplete]);

  useMapEvents({
    click: (e) => {
      if (mode === 'idle') return;

      if (mode === 'circle') {
        if (!circleCenter) {
          setCircleCenter([e.latlng.lat, e.latlng.lng]);
        } else {
          const radiusM = e.latlng.distanceTo(
            { lat: circleCenter[0], lng: circleCenter[1] } as L.LatLng,
          );
          onComplete(circleToPolygon(circleCenter[0], circleCenter[1], radiusM));
          setCircleCenter(null);
          setCircleRadius(0);
        }
        return;
      }

      const newPoint: [number, number] = [e.latlng.lat, e.latlng.lng];

      // Click-near-first-vertex closes a polygon
      if (mode === 'polygon' && vertices.length >= 3) {
        const [firstLat, firstLng] = vertices[0];
        const dist = e.latlng.distanceTo({ lat: firstLat, lng: firstLng } as L.LatLng);
        if (dist < 50) {
          finishDrawing();
          return;
        }
      }

      const next = [...vertices, newPoint];
      setVertices(next);

      // Update live measurements after click
      if (mode === 'polyline' || mode === 'corridor') {
        onMeasureUpdate?.({
          mode,
          lengthMeters: polylineLengthFromVertices(next),
          vertexCount: next.length,
        });
      } else if (mode === 'polygon') {
        const ringCoords = next.map(([lat, lng]) => [lng, lat]);
        if (ringCoords.length >= 3) {
          ringCoords.push(ringCoords[0]);
          onMeasureUpdate?.({
            mode,
            areaSqMeters: polygonArea({ type: 'Polygon', coordinates: [ringCoords] }),
            vertexCount: next.length,
          });
        } else {
          onMeasureUpdate?.({ mode, vertexCount: next.length });
        }
      }
    },
    dblclick: (e) => {
      if (mode === 'idle') return;
      e.originalEvent.preventDefault();
      // Circle: complete on double-click if center is already placed.
      // Leaflet suppresses individual click events inside a double-click sequence
      // (300ms timer disambiguation), so we must also handle completion here.
      if (mode === 'circle' && circleCenter) {
        const radiusM = e.latlng.distanceTo(
          { lat: circleCenter[0], lng: circleCenter[1] } as L.LatLng,
        );
        onComplete(circleToPolygon(circleCenter[0], circleCenter[1], radiusM));
        setCircleCenter(null);
        setCircleRadius(0);
        return;
      }
      const minVerts = mode === 'polygon' ? 3 : 2;
      if (vertices.length >= minVerts) {
        finishDrawing();
      }
    },
    mousemove: (e) => {
      if (mode === 'idle') return;

      if (mode === 'circle' && circleCenter) {
        const dist = e.latlng.distanceTo(
          { lat: circleCenter[0], lng: circleCenter[1] } as L.LatLng,
        );
        setCircleRadius(dist);
        onMeasureUpdate?.({ mode, radiusMeters: dist });
        return;
      }

      // Live preview of in-progress polyline / polygon / corridor
      if (vertices.length === 0) return;
      const cursor: [number, number] = [e.latlng.lat, e.latlng.lng];
      setHoverPoint(cursor);

      if (mode === 'polyline' || mode === 'corridor') {
        const previewVerts: [number, number][] = [...vertices, cursor];
        onMeasureUpdate?.({
          mode,
          lengthMeters: polylineLengthFromVertices(previewVerts),
          vertexCount: vertices.length,
        });
      } else if (mode === 'polygon' && vertices.length >= 2) {
        const previewVerts: [number, number][] = [...vertices, cursor];
        const ring = previewVerts.map(([lat, lng]) => [lng, lat]);
        ring.push(ring[0]);
        onMeasureUpdate?.({
          mode,
          areaSqMeters: polygonArea({ type: 'Polygon', coordinates: [ring] }),
          vertexCount: vertices.length,
        });
      }
    },
  });

  if (mode === 'idle') return null;

  // Build a "preview rope" that includes the hover point so users see
  // where the next click will land.
  const previewVertices: [number, number][] = hoverPoint && vertices.length > 0
    ? [...vertices, hoverPoint]
    : vertices;

  return (
    <>
      {/* Vertex markers */}
      {vertices.map((pos, i) => (
        <CircleMarker key={i} center={pos} radius={6}
          pathOptions={{
            color: i === 0 ? '#10B981' : '#3B82F6',
            fillColor: i === 0 ? '#10B981' : '#3B82F6',
            fillOpacity: 1, weight: 2,
          }}
        />
      ))}

      {/* Preview line including hover position */}
      {previewVertices.length >= 2 && (
        <Polyline positions={previewVertices}
          pathOptions={{ color: '#3B82F6', weight: 2, dashArray: '8 4', opacity: 0.8 }}
        />
      )}

      {/* Closing line preview for polygon */}
      {mode === 'polygon' && previewVertices.length >= 3 && (
        <Polyline positions={[previewVertices[previewVertices.length - 1], previewVertices[0]]}
          pathOptions={{ color: '#10B981', weight: 2, dashArray: '4 4', opacity: 0.5 }}
        />
      )}

      {/* Fill preview for polygon */}
      {mode === 'polygon' && previewVertices.length >= 3 && (
        <Polygon positions={previewVertices}
          pathOptions={{ color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 0.1, weight: 0 }}
        />
      )}

      {/* Corridor mode: render the buffer hint as circles around each vertex
          + segments. Cheap visual approximation while drawing — final geometry
          uses turf buffer on save. */}
      {mode === 'corridor' && previewVertices.map((pos, i) => (
        <Circle key={`buf-${i}`} center={pos} radius={corridorBufferMeters}
          pathOptions={{ color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 0.08, weight: 1, dashArray: '4 4' }}
        />
      ))}

      {/* Circle drawing */}
      {circleCenter && (
        <>
          <CircleMarker center={circleCenter} radius={6}
            pathOptions={{ color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 1 }}
          />
          {circleRadius > 0 && (
            <Circle center={circleCenter} radius={circleRadius}
              pathOptions={{ color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 0.15, dashArray: '8 4' }}
            />
          )}
        </>
      )}
    </>
  );
}
